import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import { REVIEWS } from "@/lib/oneyork";
import MobileParallax from "@/components/oneyork/MobileParallax";

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?client=safari&hs=f3NV&hl=en-ca&sca_esv=3b4cc2d9bc5e80fb&sxsrf=ANbL-n6b_9xdjd1Ux186YlOIeE45TA0KGw:1781130880661&q=one+york+auto+detailing+(+1+york+)+reviews&uds=ALYpb_lqRuhHOn9shXMP1aHnx5OhAfWZpM8Y-qg7F-bVZjL_3QlK_43co2OpZxQRmYwR4fnH49FXI7n6_zvo2TJcsUYmLhA-vGCnRQbgNpfFNaTAht6MeO6Py1Be9zh0HtCIaigbxQIDrzA9YrA1kCjSETIgkJPirrEBxqdNMk5cn06_K9D7nvuyU1db2JC9ZS3TvjuJCRBPjWugH1rDdvuplkAOofsxJR-ub9E8NBm3ckJZnYAF-M5Vi1bHqzaaH7PQ0YO6p0-GUIWknH7ub2OiK1VT3SG_9Z95Fm38uia0pJVo7S6z0fZVlmtnUoPyVA9ugkF2NjZnaVkICkZifUiQNHhPVGfKvNdxIAcXcuHRFGgQNDh35djhZBhN3XoVsHrN98Rw5wFZia_rMtaQcvxCW3invDlPUOHDitBW4iawerenmZslxmBy5YJpqQcrdjAuynvnLA553d2L5XGvGKA6b4SFmeLqNaOqiUdZc5nrFeVMl4iXdWMzQud36MMoE_C5vXlbKkM65pB6Ke6GnRpEAPim6WTOlt5DUjijTD4jIownx8Qg30U&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOWbqqUTz4uSl-IF00kS7jWCdkNm2uMBfiG8ozuqUuvXXiBpiXAtBPGXHrnokJ6e_mwYEWJksIWvq-YFQgeJiZdNlt8s9xTn3bQfxdEAWFJAIY4vaa82LyVF6YqQF7zIHecIKw98%3D&sa=X&ved=2ahUKEwiGv8LL3f2UAxWJFTQIHT1MHe4Qk8gLegQIFxAB&ictx=1&stq=1&cs=1&lei=gOYpaob_J4mr0PEPvZj18A4#ebo=4";

export default function Reviews() {
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-24 lg:py-36 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Hero photo on top of the Reviews section */}
        <MobileParallax intensity={110} dim={0.95} className="mb-12 lg:mb-16 relative z-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
          >
            <img
              data-testid="reviews-hero-image"
              src="/images/reviews-hero.jpg?v=4"
              alt="Leo and the ONE YORK Auto Detailing setup — branded vehicle, tools and equipment in York Region"
              className="w-full h-auto object-cover block"
              loading="lazy"
            />
          </motion.div>
        </MobileParallax>

        <div className="relative z-10 max-w-3xl mb-14 lg:mb-20">
          <span className="text-xs uppercase tracking-[0.28em] text-[#d4af37] font-medium">
            Reviews
          </span>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            What The{" "}
            <span className="italic font-medium text-white/70">Public</span>{" "}
            Says.
          </h2>
        </div>

        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
          {REVIEWS.map((r, i) => (
            <motion.figure
              key={r.name}
              data-testid={`review-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.05 }}
              className="border border-white/10 bg-[#0a0a0a] hover:border-white/25 transition-colors p-4 sm:p-5 lg:p-6 rounded-2xl flex flex-col"
            >
              {/* NAME + BUSINESS ON TOP */}
              <figcaption className="mb-3 pb-3 border-b border-white/10 flex items-center justify-between gap-2">
                <div className="min-w-0 flex items-baseline gap-1.5 flex-wrap">
                  <span className="font-display font-bold text-base text-white">
                    {r.name}
                  </span>
                  {r.business && (
                    <span className="text-[11px] text-[#d4af37]">
                      ({r.business})
                    </span>
                  )}
                </div>
                <div className="flex gap-0.5 shrink-0">
                  {["s1", "s2", "s3", "s4", "s5"].map((sKey) => (
                    <Star
                      key={sKey}
                      size={11}
                      className="text-[#d4af37] fill-[#d4af37]"
                    />
                  ))}
                </div>
              </figcaption>

              <blockquote className="font-body text-[13px] sm:text-sm leading-snug text-white/85 line-clamp-3 lg:line-clamp-6">
                “{r.quote}”
              </blockquote>

              <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-white/45">
                York Region
              </div>
            </motion.figure>
          ))}
        </div>

        {/* See more reviews CTA */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 lg:mt-14 flex justify-center"
        >
          <a
            data-testid="see-more-reviews-cta"
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 bg-[#d4af37] text-black font-semibold text-sm px-7 py-3.5 rounded-full border border-[#d4af37] shadow-[0_12px_30px_-8px_rgba(212,175,55,0.45)] hover:bg-[#f2c94c] hover:border-[#f2c94c] hover:scale-[1.03] active:scale-[0.98] transition-all duration-300"
          >
            <span>See more reviews on Google</span>
            <ExternalLink
              className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.25}
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
